/**
 * OlympicQuickTour — Investor/Council/Olympic-Committee Landing Page
 * ──────────────────────────────────────────────────────────────────
 *
 * A self-narrating tour of the directory tailored for the Brisbane 2032
 * Olympics pitch. Slide-by-slide flow:
 *   1. Hero — "Welcome to South East Queensland"
 *   2. Numbers we run — 25,662 entities, 4 niches, 9 districts, 12 sub-niches
 *   3. Marquee Platinum members (3 cards)
 *   4. One iconic venue from each of the 9 SEQ districts
 *   5. Safety story — Emergency strip preview
 *   6. The pitch — "Imagine your athletes & visitors using this in 2032"
 *
 * Designed to be presented full-screen with one click per section.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  ChevronDown,
  Award,
  Globe,
  ShieldCheck,
  Building2,
  MapPin,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const DISTRICTS = [
  "Brisbane", "Gold Coast", "Sunshine Coast", "Toowoomba",
  "Logan", "Moreton Bay", "Redland", "Ipswich", "Northern Rivers",
];

const SECTIONS = [
  { id: "hero",     label: "Welcome" },
  { id: "stats",    label: "By the numbers" },
  { id: "platinum", label: "Platinum showcase" },
  { id: "regions",  label: "9 districts" },
  { id: "safety",   label: "Safety first" },
  { id: "pitch",    label: "The opportunity" },
];

export default function OlympicQuickTour() {
  const [niches, setNiches] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [platinum, setPlatinum] = useState([]);
  const [districtPicks, setDistrictPicks] = useState({});
  const [activeSection, setActiveSection] = useState("hero");

  useEffect(() => {
    fetch(`${API}/api/directory/niches`).then((r) => r.json()).then((d) => setNiches(d.niches || [])).catch(() => {});
    fetch(`${API}/api/directory/districts`).then((r) => r.json()).then((d) => setDistricts(d.districts || [])).catch(() => {});
    // Platinum picks — fetch 4 well-known entities by slug
    Promise.all([
      "the-coffee-commune-bowen-hills-6163",
      "demo-trattoria-carlton-9999001",
      "oxley-cafe-oxley-14872",
      "paddington-medical-centre-qld-1000061",
    ].map((s) => fetch(`${API}/api/directory/restaurants/${s}`).then((r) => r.ok ? r.json() : null).catch(() => null)))
      .then((rows) => setPlatinum(rows.filter(Boolean)));
    // One marquee pick per district
    Promise.all(
      DISTRICTS.map((d) =>
        fetch(`${API}/api/directory/restaurants?district=${encodeURIComponent(d)}&sort=featured&limit=1`)
          .then((r) => r.json())
          .then((j) => [d, (j.results || [])[0]])
          .catch(() => [d, null])
      )
    ).then((pairs) => {
      const obj = {};
      pairs.forEach(([d, r]) => { obj[d] = r; });
      setDistrictPicks(obj);
    });
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const ids = SECTIONS.map((s) => s.id);
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top >= -100 && rect.top < 300) { setActiveSection(id); break; }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const totalEntities = niches.reduce((s, n) => s + (n.count || 0), 0);
  const restaurantsTotal = niches.find((n) => !n.key)?.count || 0;

  function scrollTo(id) {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div className="min-h-screen bg-stone-950 text-white" data-testid="olympic-page">
      {/* Side rail */}
      <nav className="hidden lg:flex flex-col gap-1 fixed left-6 top-1/2 -translate-y-1/2 z-30">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => scrollTo(s.id)}
            data-testid={`olympic-nav-${s.id}`}
            className={`text-left px-3 py-1.5 rounded-r text-[11px] uppercase tracking-wider font-bold transition-all ${
              activeSection === s.id
                ? "bg-amber-500 text-stone-950 pr-5"
                : "text-stone-400 hover:text-stone-100"
            }`}
          >
            {s.label}
          </button>
        ))}
      </nav>

      {/* ─── SECTION 1: HERO ────────────────────────────────────────── */}
      <section
        id="hero"
        className="min-h-screen relative grid place-items-center overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1566406271324-9d8b3ca28a40?w=1920&q=80"
            alt="Brisbane"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-950 via-stone-950/70 to-stone-950" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <p className="text-amber-400 text-[11px] uppercase tracking-[0.4em] font-bold mb-6 flex items-center justify-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Brisbane 2032 · Investor Tour
          </p>
          <h1 className="text-5xl md:text-7xl font-bold leading-tight">
            South East Queensland.
            <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-400">
              Hustle, mapped.
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-stone-300 mt-6 leading-relaxed">
            One directory · {totalEntities.toLocaleString()} businesses · 9 districts.
            <br />
            Every athlete, official and visitor in 2032 — sorted.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Button
              data-testid="olympic-cta-tour"
              onClick={() => scrollTo("stats")}
              size="lg"
              className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold rounded-full h-12 px-7"
            >
              Begin the tour <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Link to="/directory">
              <Button
                data-testid="olympic-cta-directory"
                size="lg"
                variant="outline"
                className="bg-transparent border-stone-600 text-white hover:bg-white/10 rounded-full h-12 px-7"
              >
                Skip to live directory
              </Button>
            </Link>
          </div>
          <div className="mt-16 animate-bounce">
            <ChevronDown className="w-6 h-6 mx-auto text-stone-500" />
          </div>
        </div>
      </section>

      {/* ─── SECTION 2: NUMBERS ─────────────────────────────────────── */}
      <section id="stats" className="py-24 bg-stone-950 relative">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-amber-400 text-[11px] uppercase tracking-[0.3em] font-bold mb-3">By the numbers</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">What we run today.</h2>
          <p className="text-stone-400 text-lg max-w-2xl mb-12">
            Not a slide deck — a live database. Every venue indexable, searchable, and bookable.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {niches.map((n) => (
              <div
                key={n.key || "restaurants"}
                data-testid={`olympic-niche-stat-${n.key || "restaurants"}`}
                className="rounded-2xl border border-stone-800 bg-stone-900/50 p-6"
              >
                <div className="text-3xl mb-2">{n.icon}</div>
                <p className="text-4xl font-bold tabular-nums">{n.count.toLocaleString()}</p>
                <p className="text-stone-400 text-sm mt-1">{n.label}</p>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-3 gap-6 mt-8">
            <div className="rounded-2xl border border-stone-800 bg-stone-900/50 p-6 flex items-center gap-4">
              <Building2 className="w-10 h-10 text-amber-400" />
              <div>
                <p className="text-3xl font-bold tabular-nums">9</p>
                <p className="text-stone-400 text-sm">SEQ districts</p>
              </div>
            </div>
            <div className="rounded-2xl border border-stone-800 bg-stone-900/50 p-6 flex items-center gap-4">
              <Globe className="w-10 h-10 text-amber-400" />
              <div>
                <p className="text-3xl font-bold tabular-nums">50+</p>
                <p className="text-stone-400 text-sm">Sub-niches indexed</p>
              </div>
            </div>
            <div className="rounded-2xl border border-stone-800 bg-stone-900/50 p-6 flex items-center gap-4">
              <Award className="w-10 h-10 text-amber-400" />
              <div>
                <p className="text-3xl font-bold tabular-nums">{restaurantsTotal.toLocaleString()}</p>
                <p className="text-stone-400 text-sm">Restaurants & cafes</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SECTION 3: PLATINUM ────────────────────────────────────── */}
      <section id="platinum" className="py-24 bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-amber-400 text-[11px] uppercase tracking-[0.3em] font-bold mb-3">Platinum showcase</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Premium tenants. Live websites.</h2>
          <p className="text-stone-400 text-lg max-w-2xl mb-12">
            One click into any card opens a fully-themed, bookable website — not a placeholder. Each pays a monthly subscription.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {platinum.map((p) => (
              <Link
                key={p.slug}
                to={`/r/${p.slug}`}
                data-testid={`olympic-platinum-${p.slug}`}
                className="group rounded-2xl overflow-hidden border border-stone-800 bg-stone-900 hover:border-amber-500/60 transition-all"
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img
                    src={p.hero_image_url}
                    alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-white text-[10px] font-bold flex items-center gap-1 shadow-lg"
                    style={{ background: "linear-gradient(135deg, #1f1f1f 0%, #4a3b1f 50%, #b08a3e 100%)" }}>
                    <Sparkles className="w-3 h-3" /> PLATINUM
                  </div>
                </div>
                <div className="p-4">
                  <p className="font-bold text-[15px]">{p.name}</p>
                  <p className="text-stone-400 text-[12px] mt-0.5">
                    {p.sub_niche || p.cuisine || p.niche} · {p.suburb || p.district}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SECTION 4: 9 DISTRICTS ─────────────────────────────────── */}
      <section id="regions" className="py-24 bg-stone-950">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-amber-400 text-[11px] uppercase tracking-[0.3em] font-bold mb-3">One platform · 9 regions</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Every district. Every venue.</h2>
          <p className="text-stone-400 text-lg max-w-2xl mb-12">
            From Brisbane CBD to Northern Rivers, indexed and ready for crawlers.
          </p>
          <div className="grid md:grid-cols-3 gap-5">
            {DISTRICTS.map((d) => {
              const pick = districtPicks[d];
              const count = districts.find((x) => x.key === d)?.count;
              return (
                <div
                  key={d}
                  data-testid={`olympic-district-${d.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-2xl overflow-hidden border border-stone-800 bg-stone-900"
                >
                  <div className="aspect-[16/9] relative">
                    {pick?.hero_image_url ? (
                      <img src={pick.hero_image_url} alt={d} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-stone-800 grid place-items-center text-stone-600">
                        <Building2 className="w-12 h-12" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                      <div>
                        <p className="text-amber-400 text-[10px] uppercase tracking-wider font-bold flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {d}
                        </p>
                        <p className="text-white text-xl font-bold leading-tight">{pick?.name || "—"}</p>
                      </div>
                      {count != null && (
                        <p className="text-white/90 text-[11px] font-bold">{count.toLocaleString()} venues</p>
                      )}
                    </div>
                  </div>
                  {pick && (
                    <Link
                      to={`/directory?district=${encodeURIComponent(d)}`}
                      className="block px-4 py-3 text-[12px] text-stone-300 hover:bg-stone-800 border-t border-stone-800"
                    >
                      Browse {d} <ChevronRight className="inline w-3 h-3" />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── SECTION 5: SAFETY ──────────────────────────────────────── */}
      <section id="safety" className="py-24 bg-gradient-to-b from-stone-950 to-red-950/40">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <ShieldCheck className="w-12 h-12 text-amber-400 mx-auto mb-6" />
          <p className="text-amber-400 text-[11px] uppercase tracking-[0.3em] font-bold mb-3">Safety first</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Emergency numbers, always one tap away.
          </h2>
          <p className="text-stone-300 text-lg leading-relaxed mb-10">
            Every page of the directory features a fixed strip with Police · Ambulance · Fire — plus the closest emergency hospital based on the visitor's location. For tourists and athletes who don't know who to call, this is the first thing they see.
          </p>
          <div className="grid grid-cols-3 gap-3 max-w-lg mx-auto">
            {["Police", "Ambulance", "Fire"].map((s) => (
              <div key={s} className="rounded-2xl border border-red-800/50 bg-red-950/50 p-5">
                <p className="text-[11px] uppercase tracking-wider font-bold text-red-300">{s}</p>
                <p className="text-4xl font-bold mt-1 font-mono">000</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SECTION 6: PITCH ───────────────────────────────────────── */}
      <section id="pitch" className="py-24 bg-stone-950">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-amber-400 text-[11px] uppercase tracking-[0.3em] font-bold mb-3">The opportunity</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            By 2032, every visitor will need this.
            <br />
            <span className="text-amber-400">It already runs.</span>
          </h2>
          <p className="text-stone-300 text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            We've built the infrastructure: data, identity, payments, bookings, SEO, schema, Calendly, HustleCRM. Add the official 2032 brand and you have a destination platform for 7 million expected visitors.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/directory">
              <Button
                data-testid="olympic-cta-end-directory"
                size="lg"
                className="bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold rounded-full h-14 px-8 text-base"
              >
                Open the live directory <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/sales">
              <Button
                data-testid="olympic-cta-pitch"
                size="lg"
                variant="outline"
                className="bg-transparent border-stone-600 text-white hover:bg-white/10 rounded-full h-14 px-8 text-base"
              >
                Read the pitch deck
              </Button>
            </Link>
          </div>
          <p className="text-stone-600 text-[12px] mt-12">
            Built in SE QLD, for SE QLD. Local Hustle Directory · Powered by HustlePlate.
          </p>
        </div>
      </section>

      {/* Floating "skip to directory" link bottom-right */}
      <Link
        to="/directory"
        className="fixed bottom-6 right-6 z-40 hidden md:flex items-center gap-2 px-4 py-2.5 rounded-full bg-amber-500 text-stone-950 font-bold shadow-2xl hover:bg-amber-400 transition-colors"
        data-testid="olympic-floating-directory"
      >
        Skip to directory <ChevronRight className="w-4 h-4" />
      </Link>
    </div>
  );
}
